import { Directive, ElementRef, forwardRef, inject, input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { formatarMoeda } from '../../shared/utils/formatadores';

/** Máscara aplicada enquanto o usuário digita. */
export type VivestFieldFormat =
  | 'none'
  | 'cpf'
  | 'digits'
  | 'conta'
  | 'agencia'
  | 'alfanumerico'
  | 'ddd'
  | 'cep'
  | 'celular'
  | 'telefone'
  | 'data'
  | 'moeda'
  | 'decimal';

/** Superfície dos web components do design system que este acessor manipula. */
type VivestFieldElement = HTMLElement & { value: string; disabled: boolean };

/**
 * Liga os web components `vivest-input`, `vivest-select` e `vivest-autocomplete` ao Reactive Forms.
 *
 * Os componentes do design system são Lit elements, então não implementam
 * ControlValueAccessor por conta própria: esta diretiva escreve/lê a propriedade
 * `value` do host e escuta os eventos que atravessam o shadow DOM
 * (`input`, disparado pelo vivest-input e vivest-autocomplete, e `change`,
 * disparado pelo vivest-select e vivest-autocomplete).
 */
@Directive({
  selector:
    'vivest-input[formControl], vivest-input[formControlName], vivest-select[formControl], vivest-select[formControlName], vivest-autocomplete[formControl], vivest-autocomplete[formControlName]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VivestControl),
      multi: true,
    },
  ],
  host: {
    '(input)': 'handleValueChange()',
    '(change)': 'handleValueChange()',
    '(focusout)': 'handleBlur()',
  },
})
export class VivestControl implements ControlValueAccessor {
  readonly format = input<VivestFieldFormat>('none', { alias: 'vivestFormat' });
  /** Limite de caracteres aplicado após a máscara. Sem valor, não há limite. */
  readonly maxLength = input<number | undefined>(undefined, { alias: 'vivestMaxLength' });

  /** Casas decimais aceitas no formato 'decimal' (ex.: 2 → "2,55"). */
  readonly casasDecimais = input<number>(2, { alias: 'vivestCasasDecimais' });

  private readonly element: VivestFieldElement = inject(ElementRef).nativeElement;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string | null): void {
    this.element.value = value ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.element.disabled = isDisabled;
  }

  protected handleValueChange(): void {
    const maxLength = this.maxLength();
    const masked = applyFormat(this.element.value, this.format(), this.casasDecimais());
    const formatted = maxLength === undefined ? masked : masked.slice(0, maxLength);

    // Devolve o valor mascarado para o campo antes de propagar ao form.
    if (formatted !== this.element.value) {
      this.element.value = formatted;
    }

    this.onChange(formatted);
  }

  protected handleBlur(): void {
    this.onTouched();
  }
}

/** Aplica a máscara do campo; exportada para formatar valores preenchidos por código (ex.: dados da API). */
export function applyFormat(value: string, format: VivestFieldFormat, casasDecimais = 2): string {
  switch (format) {
    case 'cpf':
      return formatCpf(value);
    case 'digits':
      return value.replace(/\D/g, '');
    case 'conta':
      return formatConta(value);
    case 'agencia':
      return formatAgencia(value);
    case 'alfanumerico':
      return value.replace(/[^a-zA-Z0-9]/g, '');
    case 'ddd':
      return value.replace(/\D/g, '').slice(0, 2);
    case 'cep':
      return formatCep(value);
    case 'celular':
      return formatCelular(value);
    case 'telefone':
      return formatTelefone(value);
    case 'data':
      return formatData(value);
    case 'moeda':
      return formatMoeda(value);
    case 'decimal':
      return formatDecimal(value, casasDecimais);
    default:
      return value;
  }
}

/** Formata progressivamente como 000.000.000-00. */
function formatCpf(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);

  return digits
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
}

/**
 * Conta bancária com dígito opcional (ex.: 1234-5): aceita números e um único hífen, que só entra
 * depois de algum número (hífen no início é descartado) e é seguido de um dígito.
 */
function formatConta(value: string): string {
  const [numero = '', ...resto] = value
    .replace(/[^\dXx-]/g, '')
    .replace(/^-+/, '')
    .split('-');
  const digitos = numero.replace(/\D/g, '');
  if (resto.length === 0) {
    return digitos;
  }
  // O dígito verificador pode ser X (ex.: Banco do Brasil); só ele aceita letra.
  return `${digitos}-${resto.join('').slice(0, 1).replace(/[^\dXx]/g, '').toUpperCase()}`;
}

/**
 * Agência com dígito opcional (ex.: 1234-5 ou 1234-X): até 5 dígitos e, depois do hífen, um dígito ou X. Mesmo molde
 * da conta; o número cabe em 5 posições (o campo tem 7 caracteres com o hífen e o dígito).
 */
function formatAgencia(value: string): string {
  const [numero = '', ...resto] = value
    .replace(/[^\dXx-]/g, '')
    .replace(/^-+/, '')
    .split('-');
  const digitos = numero.replace(/\D/g, '').slice(0, 5);
  if (resto.length === 0) {
    return digitos;
  }
  return `${digitos}-${resto.join('').slice(0, 1).replace(/[^\dXx]/g, '').toUpperCase()}`;
}

/** Formata progressivamente como 00000-000. */
function formatCep(value: string): string {
  return value.replace(/\D/g, '').slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2');
}

/** Formata progressivamente como 00000-0000 (sem DDD, que fica em campo próprio). */
function formatCelular(value: string): string {
  return value.replace(/\D/g, '').slice(0, 9).replace(/^(\d{5})(\d)/, '$1-$2');
}

/** Formata progressivamente como 0000-0000 (sem DDD, que fica em campo próprio). */
function formatTelefone(value: string): string {
  return value.replace(/\D/g, '').slice(0, 8).replace(/^(\d{4})(\d)/, '$1-$2');
}

/** Formata progressivamente como 00/00/0000. */
function formatData(value: string): string {
  return value
    .replace(/\D/g, '')
    .slice(0, 8)
    .replace(/^(\d{2})(\d)/, '$1/$2')
    .replace(/^(\d{2})\/(\d{2})(\d)/, '$1/$2/$3');
}

/**
 * Formata como número decimal com vírgula (ex.: "2,5"), aceitando ponto como separador e até
 * `casas` casas decimais. Sem vírgula, fica só a parte inteira.
 */
function formatDecimal(texto: string, casas: number): string {
  const limpo = texto.replace(/\./g, ',').replace(/[^\d,]/g, '');
  const [inteiro, ...resto] = limpo.split(',');
  if (resto.length === 0 || casas <= 0) {
    return inteiro;
  }
  return `${inteiro || '0'},${resto.join('').slice(0, casas)}`;
}

/** Limite de dígitos da moeda (até 999.999.999,99), para não perder precisão no número. */
const MOEDA_MAX_DIGITOS = 11;

/**
 * Formata como valor em reais (R$ 6.087,87). Os dígitos entram pela direita,
 * como em caixa eletrônico: 5 → R$ 0,05; 608787 → R$ 6.087,87. Sem dígitos, o campo fica vazio.
 */
function formatMoeda(value: string): string {
  const digits = value.replace(/\D/g, '').replace(/^0+/, '').slice(0, MOEDA_MAX_DIGITOS);

  return digits ? formatarMoeda(Number(digits) / 100) : '';
}
